import { Pressable, SxProp, View } from 'dripsy';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@react-navigation/native';
import { LayoutContext } from 'contexts/LayoutContext';
import { CountrySelectorDropdownMenu } from '@/features/card-account/components/countrySelector/CountrySelectorDropdownMenu';
import { useCountrySelector, CountryData } from '@/features/card-account/components/countrySelector/useCountrySelector';
import { UniInput } from '@/design-system/components/inputs';
import { TextBody } from '@/design-system/components/typography';
import { Icon } from '@/design-system/components/icons/Icon';

interface CountrySelectorProps {
  value?: string;
  sx?: SxProp;
  onValueChange: (value: string) => void;
  onCountryChange?: (value: CountryData | undefined) => void;
}

export const CountrySelector: React.FC<CountrySelectorProps> = ({ value, sx, onValueChange, onCountryChange }) => {
  const { t } = useTranslation('earn');
  const navigation = useNavigation<any>();
  const layout = useContext(LayoutContext);

  const { hasFocus, selectedCountry, selectedCountryId, displayCountryData, changeFocus, changeSearchText, selectCountryId, isLoading } =
    useCountrySelector({
      value,
      onValueChange,
      onCountryChange
    });

  return (
    <View sx={{ ...styles.container, ...sx }}>
      {/* Button */}
      <Pressable
        onPress={() => {
          changeFocus(true);
        }}>
        {hasFocus ? (
          <UniInput
            hasFocus={hasFocus}
            prefixIcon="search"
            onValueChange={(value) => changeSearchText(value || '')}
            placeholder={t('earn-invest::glossary.search-account')}
            wrapperSx={{ flexGrow: 1 }}
            sx={{ width: '100%', height: '$15' }}
          />
        ) : null}
        {!hasFocus && !selectedCountryId ? (
          <View sx={styles.accountButton}>
            <TextBody sx={{ color: '$textPlaceholder' }}>{t('earn-invest::glossary.select-account')}</TextBody>
            <Icon name={layout.isMobile ? 'arrowRight' : 'arrowDown'} />
          </View>
        ) : null}
        {!hasFocus && selectedCountryId ? (
          <View sx={styles.accountButton}>
            <View>
              <TextBody label>{selectedCountry?.name}</TextBody>
            </View>
            <Icon name={layout.isMobile ? 'arrowRight' : 'arrowDown'} />
          </View>
        ) : null}
      </Pressable>
      {/* Dropdown Menu */}
      <CountrySelectorDropdownMenu
        hasFocus={hasFocus}
        displayCountryData={displayCountryData}
        changeFocus={changeFocus}
        selectCountryId={selectCountryId}
        onValueChange={onValueChange}
        isLoading={isLoading}
      />
    </View>
  );
};

enum StyleName {
  container = 'container',
  accountButton = 'accountButton',
  dropdownMenu = 'dropdownMenu',
  dropdownItem = 'dropdownItem'
}

const styles: Record<StyleName, SxProp> = {
  container: {
    mb: '$5',
    zIndex: 10
  },
  accountButton: {
    height: '$15',
    paddingX: '$4',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '$borderField',
    borderRadius: 5
  },
  dropdownMenu: {
    position: 'absolute',
    top: '$15',
    left: 0,
    width: '100%',
    maxHeight: 250,
    paddingY: '$2',
    backgroundColor: '$backgroundPopup',
    borderRadius: 5,
    shadowColor: '#000000',
    shadowOffset: {
      width: 0,
      height: 2
    },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 5,
    zIndex: 10
  },
  dropdownItem: {
    paddingX: '$3',
    paddingY: '$4',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '$3'
  }
};
